import { forwardRef, RefAttributes, type JSX } from "react";

import dynamic from "next/dynamic";
import { FieldValues } from "react-hook-form";

import { FormTextFieldProps } from "../TextField/types";

export interface EmailTextFieldProps<T extends FieldValues> extends FormTextFieldProps<T> {
  checkMisspelledEmail?: boolean;
  variant?: "error" | "warning";
}

export type EmailTextFieldSsrProps<T extends FieldValues> = EmailTextFieldProps<T> & {
  forwardedRef?: React.Ref<HTMLInputElement>;
};

// Disabled SSR to avoid memory leak caused by styled
const DynamicDashboardEmailTextField = dynamic(
  () => import("./DashboardEmailTextFieldSsr").then((mod) => mod.DashboardEmailTextFieldSsr),
  { ssr: false }
) as <T extends FieldValues>(props: EmailTextFieldSsrProps<T>) => JSX.Element;

// Wrap dynamic component with forwardRef and pass ref as forwardedRef prop
// This is necessary because next/dynamic doesn't properly forward refs
const DashboardEmailTextFieldInner = forwardRef(function DashboardEmailTextFieldInner<T extends FieldValues>(
  props: EmailTextFieldProps<T>,
  ref: React.Ref<HTMLInputElement>
) {
  return <DynamicDashboardEmailTextField {...props} forwardedRef={ref} />;
});

export const DashboardEmailTextField = DashboardEmailTextFieldInner as (<T extends FieldValues>(
  props: EmailTextFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element) & {
  displayName: string;
};

DashboardEmailTextField.displayName = "DashboardEmailTextField";
